import isEqual from "lodash/isEqual";
import {
  FieldValues,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from "react-hook-form";
import { IdNameList } from "../../api/services/common/models";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import {
  checkPercentageRangeError,
  getNameByIdFromList,
  getValueOrBlankArray,
  getValueWith2DecimalDigits,
  isEmpty,
} from "../../helpers/utils";
import { KeyValueMap } from "../../redux/slices/appState/models";
import { ChipOptionsDataType } from "../chip/MuiChip";
import { OptionsDataType } from "../select/MuiSelect";
import { valueWithoutFormatInFloat } from "../textFieldCurrency/CurrencyUtils";
import {
  BillingScheduleActivities,
  BillingTypeMilestoneConfigureTableData,
  BillingTypeScheduleConfigureTableData,
} from "./modelsAndSchemas/Models";

type EntryType =
  | BillingTypeScheduleConfigureTableData
  | BillingTypeMilestoneConfigureTableData
  | null;

export const validateAmountValue = (
  amountValue: string,
  fieldKey: string,
  totalFee: string,
  messageExact: string,
  messageGreater: string,
  setError: UseFormSetError<FieldValues>,
): boolean => {
  const amountValueNumber = valueWithoutFormatInFloat(amountValue);
  const totalFeeNumber = valueWithoutFormatInFloat(totalFee);

  if (amountValueNumber > totalFeeNumber) {
    const isExactMatch = amountValueNumber === totalFeeNumber;
    const errorMessage = isExactMatch ? messageExact : messageGreater;

    setError(fieldKey, {
      type: "custom",
      message: errorMessage,
    });

    return true;
  }

  return false;
};

export const validateField = (
  fieldValue: string | number,
  fieldKey: string,
  message: string,
  setError: UseFormSetError<FieldValues>,
) => {
  if (isEmpty(fieldValue)) {
    setError(fieldKey, {
      type: "custom",
      message: message,
    });
  }
};

export const getRemainingPercentage = (
  selectedEntryForEdit: EntryType,
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
) => {
  const percentageOfEditRow = Number(selectedEntryForEdit?.percentage ?? "0");
  const addedPercentage = filteredResults
    .map((item) => Number(item.percentage))
    .reduce((acc, percentage) => acc + percentage, 0);
  return 100 - (addedPercentage - percentageOfEditRow);
};

export const copyBillingDataToLocal = (
  isBillingTypeScheduleBase: boolean,
  billingSchedulesValue:
    | BillingTypeScheduleConfigureTableData
    | BillingTypeMilestoneConfigureTableData
    | null,
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
  billingSchedulesKey: string,
  setValue: UseFormSetValue<FieldValues>,
  setFilteredResults: (
    value:
      | BillingTypeScheduleConfigureTableData[]
      | BillingTypeMilestoneConfigureTableData[],
  ) => void,
  forceUpdate: () => void,
) => {
  if (!isEqual(filteredResults, billingSchedulesValue)) {
    const mapScheduleData = (
      item: BillingTypeScheduleConfigureTableData,
    ): BillingTypeScheduleConfigureTableData => {
      const formattedAmount = getValueWith2DecimalDigits(item.amount);

      return {
        id: item?.id,
        description: item.description,
        percentage: item.percentage,
        amount: formattedAmount,
        amountWithFormat: formattedAmount,
        frequency: item.frequency,
        order: item.order,
        statusId: item.statusId,
      };
    };

    const mapMilestoneData = (
      item: BillingTypeMilestoneConfigureTableData,
    ): BillingTypeMilestoneConfigureTableData => {
      const topics = getValueOrBlankArray(item?.topics);
      const topicsIds = topics.length
        ? topics.map((activity: BillingScheduleActivities) => activity.id)
        : getValueOrBlankArray(item?.topicIds);

      const topicName: ChipOptionsDataType[] = topics.length
        ? topics.map((topic: BillingScheduleActivities) => ({
            label: topic.name,
            value: topic.name,
          }))
        : getValueOrBlankArray(item?.topicName);

      const formattedAmount = getValueWith2DecimalDigits(item.amount);
      return {
        id: item.id,
        description: item.description,
        percentage: item.percentage,
        amount: formattedAmount,
        amountWithFormat: formattedAmount,
        topicIds: topicsIds.filter((id): id is string => id !== undefined),
        topicName: topicName,
        order: item.order,
        topics: item.topics,
        statusId: item.statusId,
      };
    };

    let filteredResultsCopy:
      | BillingTypeScheduleConfigureTableData[]
      | BillingTypeMilestoneConfigureTableData[] = [];
    if (billingSchedulesValue) {
      if (isBillingTypeScheduleBase) {
        filteredResultsCopy = billingSchedulesValue.map(mapScheduleData);
      } else {
        filteredResultsCopy = billingSchedulesValue.map(mapMilestoneData);
      }
    }

    sortFilterData(filteredResultsCopy);
    setFilteredResults(filteredResultsCopy);
    addEntriesToFormKey(
      billingSchedulesKey,
      setValue,
      filteredResultsCopy,
      forceUpdate,
    );
  }
};

export const addEntriesToFormKey = (
  billingSchedulesKey: string,
  setValue: UseFormSetValue<FieldValues>,
  results:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
  forceUpdate: () => void,
) => {
  setValue(billingSchedulesKey, results);
  forceUpdate();
};

export const sortFilterData = (
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
) => {
  filteredResults.sort((a, b) => {
    return a.order - b.order;
  });
};

export const getDeleteMessageBasedOnBillingType = (
  isBillingTypeScheduleBase: boolean,
  deleteMessageSchedule: string,
  deleteMessageMilestone: string,
) => {
  if (isBillingTypeScheduleBase) {
    return deleteMessageSchedule;
  } else {
    return deleteMessageMilestone;
  }
};

export interface BillingTypeCommonConfigParams {
  isSchedulePeriodMonthly: boolean;
  isValueTypePercentage: boolean;
  feeKey: string;
  feePercentageKey: string;
  amountWithFormatKey: string;
  amountKey: string;
  isFromProject: boolean;
  getValues: UseFormGetValues<FieldValues>;
  setError: UseFormSetError<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
}

export interface BillingTypeScheduleConfigParams
  extends BillingTypeCommonConfigParams {
  isBillingTypeScheduleBase: boolean;
  scheduleKey: string;
  scheduleFrequencyKey: string;
}

export interface BillingTypeMilestoneConfigParams
  extends BillingTypeCommonConfigParams {
  isBillingTypeMilestoneBase: boolean;
  milestoneKey: string;
  activitiesKey: string;
  topicsList?: IdNameList[];
}

export const validateBillingTypeScheduleBase = (
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
  selectedEntryForEdit: EntryType,
  scheduleConfigParams: BillingTypeScheduleConfigParams,
  fetchValidationMessage: (key: string, replacement?: KeyValueMap) => string,
  fetchDropdownValues: (
    parentKey: string,
    key: string,
    optionByValue?: boolean,
  ) => OptionsDataType[],
  commonBillingConfigurationDetails: { [key: string]: any },
): BillingTypeScheduleConfigureTableData | null => {
  const {
    isBillingTypeScheduleBase,
    isValueTypePercentage,
    scheduleKey,
    feeKey,
    feePercentageKey,
    amountWithFormatKey,
    amountKey,
    scheduleFrequencyKey,
    isFromProject,
    getValues,
    setError,
    clearErrors,
  } = scheduleConfigParams;
  if (!isBillingTypeScheduleBase) return null;
  const fieldRequiredMessage = fetchValidationMessage(
    commonSiteCoreKeys.requiredValidation,
  );

  const amountCannotGreaterThanFeeErrorMessage = isFromProject
    ? commonBillingConfigurationDetails.amountCannotGreaterThanProjectFeeErrorMessage
    : commonBillingConfigurationDetails.amountCannotGreaterThanProgramFeeErrorMessage;

  const schedule = getValues(scheduleKey);
  const feePercentage = getValues(feePercentageKey);
  const amountWithFormat = getValues(amountWithFormatKey);
  const amount = getValues(amountKey);
  const scheduleFrequency = getValues(scheduleFrequencyKey);

  validateField(schedule, scheduleKey, fieldRequiredMessage, setError);

  validateField(
    scheduleFrequency,
    scheduleFrequencyKey,
    fieldRequiredMessage,
    setError,
  );

  if (isValueTypePercentage) {
    validateField(
      feePercentage,
      feePercentageKey,
      fieldRequiredMessage,
      setError,
    );
  } else {
    validateField(
      amountWithFormat,
      amountWithFormatKey,
      fieldRequiredMessage,
      setError,
    );
  }

  if (
    isEmpty(schedule) ||
    (isValueTypePercentage
      ? isEmpty(feePercentage)
      : isEmpty(amountWithFormat)) ||
    isEmpty(scheduleFrequency) ||
    (isValueTypePercentage
      ? validatePercentageValue(
          feePercentage,
          feePercentageKey,
          setError,
          fetchValidationMessage,
        )
      : validateAmountValue(
          amountWithFormat,
          amountWithFormatKey,
          getValues(feeKey),
          commonBillingConfigurationDetails.pleaseCreateMoreThanOneScheduleAndDistributeTheAmountAccordingly,
          amountCannotGreaterThanFeeErrorMessage,
          setError,
        )) ||
    (isValueTypePercentage
      ? validatePercentageWithRemainingValue(
          filteredResults,
          selectedEntryForEdit,
          feePercentageKey,
          commonBillingConfigurationDetails.totalPercentageCannotExceed100,
          getValues,
          setError,
          clearErrors,
        )
      : validateAmountWithRemainingValue(
          filteredResults,
          selectedEntryForEdit,
          scheduleConfigParams,
          commonBillingConfigurationDetails.totalAmountMustEqualProgramFeeErrorMessage,
          commonBillingConfigurationDetails.totalAmountMustEqualProjectFeeErrorMessage,
        ))
  ) {
    return null;
  }

  if (
    validateFrequencyAlreadyExist(
      filteredResults,
      scheduleConfigParams,
      schedule,
      scheduleFrequency,
      setError,
      fetchDropdownValues,
      commonBillingConfigurationDetails,
    )
  ) {
    return null;
  }

  return {
    id: selectedEntryForEdit?.id,
    description: schedule,
    percentage: feePercentage,
    amount,
    amountWithFormat,
    frequency: scheduleFrequency,
    order: selectedEntryForEdit
      ? selectedEntryForEdit.order
      : filteredResults.length + 1,
  };
};

export const validateBillingTypeMilestone = (
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
  selectedEntryForEdit: EntryType,
  milestoneConfigParams: BillingTypeMilestoneConfigParams,

  fetchValidationMessage: (key: string, replacement?: KeyValueMap) => string,
  commonBillingConfigurationDetails: { [key: string]: any },
): BillingTypeMilestoneConfigureTableData | null => {
  const {
    isBillingTypeMilestoneBase,
    isValueTypePercentage,
    milestoneKey,
    feeKey,
    feePercentageKey,
    amountWithFormatKey,
    amountKey,
    activitiesKey,
    isFromProject,
    topicsList,
    getValues,
    setError,
    clearErrors,
  } = milestoneConfigParams;
  if (!isBillingTypeMilestoneBase) return null;
  const fieldRequiredMessage = fetchValidationMessage(
    commonSiteCoreKeys.requiredValidation,
  );

  const amountCannotGreaterThanFeeErrorMessage = isFromProject
    ? commonBillingConfigurationDetails.amountCannotGreaterThanProjectFeeErrorMessage
    : commonBillingConfigurationDetails.amountCannotGreaterThanProgramFeeErrorMessage;

  const milestone = getValues(milestoneKey);
  const feePercentage = getValues(feePercentageKey);
  const amountWithFormat = getValues(amountWithFormatKey);
  const amount = getValues(amountKey);
  const topics = getValues(activitiesKey);

  validateField(milestone, milestoneKey, fieldRequiredMessage, setError);
  validateField(
    amountWithFormat,
    amountWithFormatKey,
    fieldRequiredMessage,
    setError,
  );
  validateField(topics, activitiesKey, fieldRequiredMessage, setError);

  if (isValueTypePercentage) {
    validateField(
      feePercentage,
      feePercentageKey,
      fieldRequiredMessage,
      setError,
    );
  }
  if (
    isEmpty(milestone) ||
    (isValueTypePercentage
      ? isEmpty(feePercentage)
      : isEmpty(amountWithFormat)) ||
    isEmpty(topics) ||
    (isValueTypePercentage
      ? validatePercentageValue(
          feePercentage,
          feePercentageKey,
          setError,
          fetchValidationMessage,
        )
      : validateAmountValue(
          amountWithFormat,
          amountWithFormatKey,
          getValues(feeKey),
          commonBillingConfigurationDetails.pleaseCreateMoreThanOneScheduleAndDistributeTheAmountAccordingly,
          amountCannotGreaterThanFeeErrorMessage,
          setError,
        )) ||
    (isValueTypePercentage
      ? validatePercentageWithRemainingValue(
          filteredResults,
          selectedEntryForEdit,
          feePercentageKey,
          commonBillingConfigurationDetails.totalPercentageCannotExceed100,
          getValues,
          setError,
          clearErrors,
        )
      : validateAmountWithRemainingValue(
          filteredResults,
          selectedEntryForEdit,
          milestoneConfigParams,
          commonBillingConfigurationDetails.totalAmountMustEqualProgramFeeErrorMessage,
          commonBillingConfigurationDetails.totalAmountMustEqualProjectFeeErrorMessage,
        ))
  ) {
    return null;
  }

  const selectedTopicType: ChipOptionsDataType[] = topics.map(
    (activity: string) => {
      const finalValue = getNameByIdFromList(topicsList, activity);

      return {
        label: finalValue,
        value: finalValue,
      };
    },
  );

  return {
    id: selectedEntryForEdit?.id,
    description: milestone,
    percentage: feePercentage,
    amount,
    amountWithFormat,
    topicIds: topics,
    topicName: selectedTopicType,
    order: selectedEntryForEdit
      ? selectedEntryForEdit.order
      : filteredResults.length + 1,
  };
};

const validateFrequencyAlreadyExist = (
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
  configParams: BillingTypeScheduleConfigParams,
  schedule: string,
  selectedFrequencyValue: string,
  setError: UseFormSetError<FieldValues>,
  fetchDropdownValues: (
    parentKey: string,
    key: string,
    optionByValue?: boolean,
  ) => OptionsDataType[],
  commonBillingConfigurationDetails: { [key: string]: any },
) => {
  const { isSchedulePeriodMonthly, scheduleFrequencyKey } = configParams;
  if (isEmpty(filteredResults)) return false;

  const scheduleFrequencyDropdownKey = isSchedulePeriodMonthly
    ? commonSiteCoreKeys.scheduleFrequencyMonthlyList
    : commonSiteCoreKeys.scheduleFrequencyWeeklyList;

  const frequencyOptions = fetchDropdownValues(
    commonSiteCoreKeys.billingConfigurationDetails,
    scheduleFrequencyDropdownKey,
  );

  const scheduleIndex = filteredResults.findIndex(
    (item) => item.description === schedule,
  );

  const selectedFrequencyValueIndex = frequencyOptions.findIndex(
    (item) => item.value === selectedFrequencyValue,
  );

  const validateEntry = (
    entry: (typeof filteredResults)[number] | undefined,
    comparator: (entryIndex: number, selectedIndex: number) => boolean,
  ) => {
    if (!entry) return false;

    const entryValueIndex = frequencyOptions.findIndex(
      (item) => item.value === entry.frequency,
    );

    if (comparator(entryValueIndex, selectedFrequencyValueIndex)) {
      setError(scheduleFrequencyKey, {
        type: "custom",
        message:
          commonBillingConfigurationDetails.selectedFrequencyMustBeLowerThanThePreviouslyAddedSchedules,
      });
      return true;
    }

    return false;
  };

  const validateWithNeighbors = (index: number) => {
    const previousEntry = filteredResults[index - 1];
    const nextEntry = filteredResults[index + 1];

    return (
      validateEntry(
        previousEntry,
        (prevIndex, selectedIndex) => prevIndex >= selectedIndex,
      ) ||
      validateEntry(
        nextEntry,
        (nextIndex, selectedIndex) => nextIndex <= selectedIndex,
      )
    );
  };

  if (scheduleIndex >= 0) {
    return validateWithNeighbors(scheduleIndex);
  }

  const lastScheduleRecord = filteredResults.at(-1);
  return validateEntry(
    lastScheduleRecord,
    (lastIndex, selectedIndex) => lastIndex >= selectedIndex,
  );
};

export const validatePercentageValue = (
  percentageValue: string,
  fieldKey: string,
  setError: UseFormSetError<FieldValues>,
  fetchValidationMessage: (key: string, replacement?: KeyValueMap) => string,
): boolean => {
  const isError = checkPercentageRangeError(percentageValue);
  if (isError) {
    setError(fieldKey, {
      type: "custom",
      message: fetchValidationMessage(
        commonSiteCoreKeys.pleaseEnterNumberBetween1To100,
      ),
    });
  }
  return isError;
};

export const validatePercentageWithRemainingValue = (
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
  selectedEntryForEdit: EntryType,
  feePercentageKey: string,
  totalPercentageCannotExceed100: string,
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  clearErrors: UseFormClearErrors<FieldValues>,
) => {
  const currentPercentage = getValues(feePercentageKey);
  const remainingPercentage = getRemainingPercentage(
    selectedEntryForEdit,
    filteredResults,
  );

  if (currentPercentage > remainingPercentage) {
    setError(feePercentageKey, {
      type: "custom",
      message: totalPercentageCannotExceed100,
    });
    return true;
  }

  clearErrors(feePercentageKey);
  return false;
};

export interface BillingTypeRemainingAmountParams {
  feeKey: string;
  amountWithFormatKey: string;
  amountKey: string;
  isFromProject: boolean;
  getValues: UseFormGetValues<FieldValues>;
  setError: UseFormSetError<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
}

export const validateAmountWithRemainingValue = (
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
  selectedEntryForEdit: EntryType,
  remainingAmountParams: BillingTypeRemainingAmountParams,
  totalAmountMustEqualProgramFeeErrorMessage: string,
  totalAmountMustEqualProjectFeeErrorMessage: string,
) => {
  const {
    amountKey,
    amountWithFormatKey,
    isFromProject,
    feeKey,
    getValues,
    setError,
    clearErrors,
  } = remainingAmountParams;
  const currentAmount = valueWithoutFormatInFloat(getValues(amountKey));
  const remainingAmount = getRemainingAmount(
    selectedEntryForEdit,
    filteredResults,
    feeKey,
    getValues,
  );

  if (currentAmount > remainingAmount) {
    setError(amountWithFormatKey, {
      type: "custom",
      message: isFromProject
        ? totalAmountMustEqualProgramFeeErrorMessage
        : totalAmountMustEqualProjectFeeErrorMessage,
    });
    return true;
  }

  clearErrors(amountWithFormatKey);
  return false;
};

const getRemainingAmount = (
  selectedEntryForEdit:
    | BillingTypeScheduleConfigureTableData
    | BillingTypeMilestoneConfigureTableData
    | null,
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[],
  feeKey: string,
  getValues: UseFormGetValues<FieldValues>,
) => {
  const amountOfEditRow = valueWithoutFormatInFloat(
    selectedEntryForEdit?.amount,
  );

  const totalFee = valueWithoutFormatInFloat(getValues(feeKey));

  const totalAddedAmount = filteredResults
    .map((item) => valueWithoutFormatInFloat(item.amount))
    .filter((amount) => !isNaN(amount))
    .reduce((acc, amount) => acc + amount, 0);

  return parseFloat(
    (totalFee - (totalAddedAmount - amountOfEditRow)).toFixed(2).toString(),
  );
};
